import React from 'react';

/**
 * An on/off switch you cannot miss.
 *
 * Used for the one setting that decides whether a configuration spends money at all, so it reads
 * as a state rather than as one checkbox among others. It is a real `role="switch"` button, so it
 * works from the keyboard and announces its state.
 */
const ToggleSwitch = ({ checked, onChange, disabled, label, description }) => {
  const active = checked && !disabled;
  // Written out rather than composed: a class built from a variable is never emitted by Tailwind.
  const track = active ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600';

  return (
    <div
      className={`flex items-center gap-4 rounded-2xl border p-4 transition-colors ${
        active
          ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30'
          : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
      }`}
    >
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${track}`}
      >
        {/* Anchored left: without it the knob starts from the button's centred static position and
            slides out of the track. */}
        <span
          className={`absolute left-0 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
        />
      </button>

      <div className="min-w-0">
        <div className={`text-sm font-semibold ${active ? 'text-emerald-900 dark:text-emerald-100' : 'text-gray-700 dark:text-gray-200'}`}>
          {label}
        </div>
        {description && <p className="mt-0.5 text-xs leading-5 text-gray-600 dark:text-gray-400">{description}</p>}
      </div>
    </div>
  );
};

export default ToggleSwitch;
